"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslate } from "../../lib/translation";
import { cn } from "../../lib/cn";
import styles from "./LanguageSwitcher.module.css";

export type LanguageSwitcherOption = {
  /** BCP 47 language code; also set as `lang` on the option button. */
  code: string;
  /** Visible label, e.g. "FI". */
  label: string;
  /**
   * The language's name, e.g. "Suomi". Not an action phrase: the trigger and
   * each option are named "<label>, <name>", so the visible label stays in
   * the name (WCAG 2.5.3); the trigger's aria-expanded carries open/closed.
   */
  ariaLabel: string;
};

const defaultButtonClassName =
  "shrink-0 whitespace-nowrap px-2.5 py-1.5 text-sm font-heading font-semibold uppercase tracking-widest transition-colors motion-reduce:transition-none cursor-pointer rounded-sm border border-transparent bg-transparent text-muted-foreground hover:text-foreground hover:bg-foreground/5";

const defaultActiveButtonClassName = "text-foreground hover:text-foreground";

/** Selected language when the menu is expanded (focused control). */
const defaultOpenTriggerClassName =
  "bg-foreground/5 border-foreground text-foreground hover:border-foreground";

/* text-foreground (not muted): the fanned-out options are interactive text
   and must meet 4.5:1 — muted-foreground measures ~2.9:1 on light surfaces. */
const defaultFloatedButtonClassName =
  "font-semibold text-foreground/80 hover:text-foreground";

export interface LanguageSwitcherProps {
  /** Available languages (code, label, accessible name). */
  languages: LanguageSwitcherOption[];
  /** Currently selected language code. */
  currentLang: string;
  /** Called with the selected language code. */
  onLanguageChange: (code: string) => void;
  /** Optional classes on the group wrapper. */
  className?: string;
  /** Base class override applied to every language button. */
  buttonClassName?: string;
  /** Class override for the current-language trigger. */
  activeButtonClassName?: string;
  /** Class override for the trigger while the tray is open. */
  openTriggerClassName?: string;
  /** Class override for the fanned-out tray options. */
  floatedButtonClassName?: string;
}

const fanSpring = {
  type: "spring" as const,
  stiffness: 300,
  damping: 30,
};

export function LanguageSwitcher({
  languages,
  currentLang,
  onLanguageChange,
  className,
  buttonClassName = defaultButtonClassName,
  activeButtonClassName = defaultActiveButtonClassName,
  openTriggerClassName = defaultOpenTriggerClassName,
  floatedButtonClassName = defaultFloatedButtonClassName,
}: LanguageSwitcherProps) {
  const t = useTranslate();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionsId = useId();
  const prefersReducedMotion = useReducedMotion();

  const current =
    languages.find((lang) => lang.code === currentLang) ?? languages[0];
  const otherLanguages = languages
    .filter((lang) => lang.code !== currentLang)
    .reverse();

  const close = useCallback((restoreTriggerFocus = false) => {
    setIsOpen(false);
    if (restoreTriggerFocus) triggerRef.current?.focus();
  }, []);

  const handleSelect = useCallback(
    (code: string) => {
      onLanguageChange(code);
      close(true);
    },
    [close, onLanguageChange],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        close();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close(true);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [close, isOpen]);

  return (
    <div
      ref={rootRef}
      className={cn(styles.root, className)}
      role="group"
      aria-label={t("navMenuLanguages", "Language")}
    >
      {/* Trigger first in DOM so that opening the tray and pressing Tab walks
          FORWARD into the revealed options. The tray is position:absolute, so
          its DOM order is decoupled from the visual left-fan — keeping it
          after the trigger fixes the focus order without moving the fan. */}
      <button
        ref={triggerRef}
        type="button"
        className={cn(
          styles.trigger,
          styles.option,
          buttonClassName,
          activeButtonClassName,
          isOpen && openTriggerClassName,
        )}
        aria-expanded={isOpen}
        aria-controls={optionsId}
        // A stable name: aria-expanded already says whether the options are
        // shown, so the name must not change with state (and voice control
        // users keep one target). The group supplies the "Language" context.
        aria-label={`${current.label}, ${current.ariaLabel}`}
        onClick={() => setIsOpen((open) => !open)}
      >
        {current.label}
      </button>

      <div
        id={optionsId}
        className={styles.optionsTrayAnchor}
        data-language-options
        data-open={isOpen ? "true" : "false"}
        aria-hidden={!isOpen}
        /* aria-hidden flips at close while the exit animation still renders
           the option buttons; inert keeps that fading window unfocusable so
           keyboard focus can never land inside a hidden tray. */
        inert={!isOpen}
      >
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              className={styles.optionsTrayFan}
              initial={
                prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: 10 }
              }
              animate={
                prefersReducedMotion ? { opacity: 1 } : { opacity: 1, x: 0 }
              }
              exit={
                prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: 8 }
              }
              transition={prefersReducedMotion ? { duration: 0.12 } : fanSpring}
            >
              {otherLanguages.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  className={cn(
                    styles.option,
                    buttonClassName,
                    floatedButtonClassName,
                  )}
                  // Starts with the visible label so voice control users can
                  // say what they see (WCAG 2.5.3); lang lets screen readers
                  // pronounce the language's own name correctly.
                  aria-label={`${lang.label}, ${lang.ariaLabel}`}
                  lang={lang.code}
                  onClick={() => handleSelect(lang.code)}
                >
                  {lang.label}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

LanguageSwitcher.displayName = "LanguageSwitcher";

export default LanguageSwitcher;
